import { useState } from "react";
import DOMPurify from "dompurify";

// Allowed URL protocols. javascript:, data:, vbscript:, file:, blob: are
// not linkified — they're rendered as plain text.
const SAFE_PROTOCOLS = /^(https?:)/i;

// Match http(s)://… or www.… up to whitespace / closing punctuation.
// Trailing punctuation that's typical sentence terminator is stripped
// so "Visit https://example.com." renders the period outside the link.
const URL_RE = /\b((?:https?:\/\/|www\.)[^\s<>"']+)/gi;

function makeSafeUrl(raw) {
  let candidate = raw;
  // Strip trailing sentence-ending punctuation that almost never belongs in a URL
  let trailing = "";
  while (candidate && /[).,;:!?]$/.test(candidate)) {
    trailing = candidate.slice(-1) + trailing;
    candidate = candidate.slice(0, -1);
  }
  let href = candidate;
  if (/^www\./i.test(candidate)) href = `https://${candidate}`;
  try {
    const u = new URL(href);
    if (!SAFE_PROTOCOLS.test(u.protocol)) return { href: null, trailing };
    return { href: u.toString(), trailing };
  } catch {
    return { href: null, trailing };
  }
}

/**
 * Safely render plain chat text with auto-linked URLs.
 * - Escapes HTML (no raw injection)
 * - Detects http(s)/www. URLs
 * - Drops unsafe protocols
 * - Opens links in a new tab with rel="noopener noreferrer nofollow"
 * - Returns sanitized HTML wrapped via DOMPurify for defense-in-depth
 */
export function renderChatText(text) {
  if (!text) return "";
  const s = String(text);
  let out = "";
  let lastIdx = 0;
  for (const match of s.matchAll(URL_RE)) {
    const raw = match[0];
    const idx = match.index ?? 0;
    out += escapeHTML(s.slice(lastIdx, idx));
    const { href, trailing } = makeSafeUrl(raw);
    if (href) {
      out += `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer nofollow" class="chat-link">${escapeHTML(raw.slice(0, raw.length - trailing.length))}</a>${escapeHTML(trailing)}`;
    } else {
      out += escapeHTML(raw);
    }
    lastIdx = idx + raw.length;
  }
  out += escapeHTML(s.slice(lastIdx));
  // DOMPurify wraps everything so even a regex bug can't produce script tags.
  // `ADD_ATTR: ["target"]` is required because DOMPurify strips
  // `target="_blank"` by default (anti-tabnapping guard) — we already
  // pair it with rel="noopener noreferrer nofollow" above, which is the
  // canonical safe combo, so it's safe to add it back.
  return DOMPurify.sanitize(out, {
    ALLOWED_TAGS: ["a", "br"],
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
    ADD_ATTR: ["target"],
    ALLOWED_URI_REGEXP: /^https?:\/\//i,
  });
}

function escapeHTML(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeAttr(s) {
  return escapeHTML(s);
}

/** Render a chat-message text block as a React node. */
export default function ChatText({ text, className = "" }) {
  // Defense-in-depth: even if DOMPurify ever strips `target="_blank"`
  // from our sanitized links, we intercept clicks on `.chat-link` and
  // force a new-tab open via window.open. This guarantees chat links
  // never navigate the meeting room tab away from the meeting.
  const onClick = (e) => {
    const a = e.target.closest && e.target.closest("a.chat-link");
    if (!a) return;
    const href = a.getAttribute("href");
    if (!href || !/^https?:\/\//i.test(href)) return;
    e.preventDefault();
    e.stopPropagation();
    window.open(href, "_blank", "noopener,noreferrer");
  };
  return (
    <p
      className={`chat-text break-words ${className}`}
      onClick={onClick}
      // We control the input through `renderChatText` + DOMPurify, so this
      // is the documented safe usage of dangerouslySetInnerHTML.
      dangerouslySetInnerHTML={{ __html: renderChatText(text) }}
    />
  );
}

// Default emoji set for chat reactions — must match the backend's
// CHAT_ALLOWED_REACTIONS list. Kept here as a constant so the picker
// can render immediately without a network round-trip on first open.
export const DEFAULT_REACTIONS = [
  "👍", "👎", "❤️", "😂", "😮", "😢", "🎉", "👀", "🙌", "🔥",
  "✅", "💡", "👏", "🤔", "🚀", "⭐", "💯", "🙏", "😎", "📝",
];

/** Format a byte size for display next to chat attachments. */
export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Hook-free state container for an emoji popover anchored to a message. */
export function useReactionPicker() {
  const [openMsgId, setOpenMsgId] = useState(null);
  return { openMsgId, setOpenMsgId };
}
